<script>
/**
 * O cabeçalho reusável de sobretítulo + título + métricas + ação no canto —
 * extraído de `cartao-casca.vue` [feedback do usuário]: "vamos usar em vários
 * lugares".
 *
 * ★ **[19/09/2026] Ele passou a ter DOIS vocabulários de verdade**, e a
 * diferença agora é estrutural, não só de escala:
 *
 * ```
 * SEÇÃO (o mock do owner, `cartao-casca`/`cartao-tabela`)
 *   CRM · OPORTUNIDADES                       Abrir kanban   ⌃   ← sobretítulo
 *   Funil de oportunidades                                       ← SERIF 28px
 *   59 abertas · 9 ganhas · pipeline R$ 17.586.716,91             ← métricas
 *
 * STATUS/AÇÃO (`acao-oferecida-card`, `fallback-card`, o erro do turno)
 *   ⚑  Abrir tela · CRM                          [Aberto]        ← uma linha
 * ```
 *
 * O primeiro é quem ABRE um bloco de conteúdo abaixo; o segundo divide a
 * MESMA linha com badge/botão e não pode crescer. Por isso o sobretítulo e a
 * serif só valem quando há `titulo` e nem `compacto` nem `fita` estão ligados
 * — a mesma condição que já escolhia `--titulo-grande`.
 *
 * ⚠️ **Clicável é `<div role="button" tabindex="0">`, e NÃO um `<button>` —
 * medido, não preferido.** A casca do onboarding usa `<button>` de verdade, e
 * a primeira versão desta extração (19/09/2026) copiou isso para cá. Está
 * errado aqui: o slot `cornerAction` carrega BOTÕES DE VERDADE — o "Abrir na
 * tela" da `cartao-casca.vue`, o "Abrir tarefas" que `tarefas-lista-card.vue`
 * passa — e `<button>` dentro de `<button>` é HTML inválido: o navegador
 * quebra o foco e o clique do botão interno vira comportamento indefinido. É a
 * mesma armadilha que o cabeçalho do onboarding evitou fazendo do chevron um
 * `<span aria-hidden>`; lá o canto só tem o chevron, aqui ele tem ação.
 *
 * O que fica é o disclosure completo por ARIA, que é o que os ~24 cartões já
 * traziam: `role="button"` + `tabindex="0"` + `aria-expanded`, com Enter e
 * Espaço ligados à mão. Para leitor de tela é indistinguível de um `<button>`.
 *
 * ⚠️ Pelo mesmo motivo NÃO há `<h3>` em volta: o modelo de conteúdo de um
 * heading é *phrasing content*, e um `<div>` ali dentro seria inválido do
 * outro lado da conta.
 *
 * ⚠️ **Sem `aria-label` e sem `title` no gatilho.** O `aria-label`
 * SUBSTITUIRIA o nome acessível, e quem usa leitor de tela ouviria "Recolher"
 * em vez do título — a única coisa que identifica ESTE cartão entre os vários
 * do thread. O `title` (balão nativo) nasce sob o cursor e tapa o conteúdo que
 * deveria ajudar a ler; saiu da casca do onboarding em 18/09/2026 pelo mesmo
 * motivo. O que fica é o disclosure completo: NOME por `aria-labelledby`,
 * ESTADO por `aria-expanded`, métricas por `aria-describedby`.
 *
 * ★ **O sobretítulo e as métricas COLAPSAM, nunca somem por `v-if`.** Sair de
 * uma vez faz o título saltar no primeiro quadro enquanto o corpo desliza por
 * 300ms — é o corolário do item 3 da receita do `CLAUDE.md` da raiz, e foi
 * exatamente a "travada" relatada na casca do onboarding. Aqui eles colapsam
 * por `grid-template-rows: 1fr → 0fr` no MESMO tempo/easing do
 * `.expand-transition-*-active` da Vuetify, junto do `transition: padding` do
 * gatilho.
 *
 * O slot da ação chama `cornerAction` — o MESMO nome que o `BaseTable` já usa
 * pro canto superior direito (`src/components/_base-table.vue`), pra manter um
 * nome só pro mesmo conceito na plataforma inteira. Ele é EMPURRADO pra
 * direita (`margin-left: auto`) — é pra uma ação de verdade (CTA + seta de
 * colapso), não pra qualquer coisa que venha depois do título.
 *
 * O slot `meta`, em vez disso, fica GRUDADO no título (mesmo gap de 8px do
 * `.ia-block-card__cabecalho`, sem margem automática) — é pra badge/status/
 * botão que hoje já aparece packed junto do nome nos cartões de STATUS/AÇÃO
 * (`acao-oferecida-card.vue`, `ia-confirm-action.vue`, o erro do turno em
 * `chat-message.vue`).
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
export default {
  name: 'CartaoCabecalho',
  props: {
    /**
     * O título em destaque. String vazia = sem título — usar o slot nomeado
     * `titulo` pra desenhar outra coisa no lugar (foi o caso do
     * `cartao-pendente.vue`, sem título, só um indicador de "carregando" —
     * apagado em 11/09/2026; o slot fica, é a API do cabeçalho).
     */
    titulo: {
      type: String,
      default: '',
    },
    /**
     * ★ [19/09/2026] O `MÓDULO · SEÇÃO` do mock do owner ("CRM · CLIENTES"),
     * uma linha ACIMA do título — caixa alta e `letter-spacing` vêm da classe
     * (`az-ds-label`), nunca da string: quem escreve a copy escreve em caixa
     * de sentença e o DS decide como ela se apresenta.
     *
     * Vazio (default) não desenha a caixa, e todo cartão que ainda não o passa
     * continua exatamente como estava.
     *
     * ⚠️ Só vale no vocabulário de SEÇÃO — em `compacto`/`fita` o cabeçalho é
     * uma linha só, e um rótulo acima dela viraria um segundo título.
     */
    /**
     * ★ [owner, 21/09/2026 — esboço da Lista de Espera] Uma linha de RESUMO
     * abaixo do título, que só aparece com o cartão RECOLHIDO ("Sem respostas",
     * "Herik · herik@…"): é o inverso das métricas (`subtitulo`), que colapsam
     * junto com o corpo. Vazio (default) não desenha nada — todo cartão que
     * não o passa continua exatamente como estava.
     */
    resumo: {
      type: String,
      default: '',
    },
    sobretitulo: {
      type: String,
      default: '',
    },
    /**
     * `data-teste` opcional no `<span>` do título — vazio (default) não
     * desenha o atributo. Existe só pra quem já tinha um marcador PRÓPRIO
     * no título antes de migrar pra este cabeçalho compartilhado (ex:
     * `acao-oferecida-card.vue`, testado via `partes-do-turno.unit.js`) e
     * não quebrar o teste por causa disso.
     */
    testeTitulo: {
      type: String,
      default: '',
    },
    /**
     * Vocabulário de STATUS/AÇÃO (uma linha só, ícone+título+badge/botão
     * dividindo espaço) em vez do vocabulário de SEÇÃO (título abre um bloco
     * de conteúdo abaixo — o default). Aplica `.ia-block-card__nome--compacto`
     * JUNTO de `.ia-block-card__nome` (não no lugar dele — o modifier só
     * sobrescreve o tamanho, herda cor/line-height, ver
     * `design/_ia-block-card.sass`).
     */
    compacto: {
      type: Boolean,
      default: false,
    },
    /** Nome de um SVG de `src/components/icons/svg/`. Vazio = sem ícone. */
    icone: {
      type: String,
      default: '',
    },
    corDoIcone: {
      type: String,
      default: 'var(--primary)',
    },
    /** O cabeçalho também é o gatilho de colapso de quem o usa. */
    clicavel: {
      type: Boolean,
      default: false,
    },
    /**
     * Só lido quando `clicavel` — vira o `aria-expanded` e decide o respiro.
     *
     * [bug corrigido] `default: true` violava `vue/no-boolean-default`.
     * Default `false` é seguro: os dois únicos consumidores hoje
     * (`cartao-casca-fluida.vue` e quem o compõe) sempre passam `:aberto`
     * explícito junto de `clicavel` — nenhum depende deste default.
     */
    aberto: {
      type: Boolean,
      default: false,
    },
    /**
     * Sem o padding lateral (14px) de sempre — [feedback do usuário] em
     * `cartao-tabela.vue`, onde o cabeçalho flutua sem caixa (não é mais
     * inset de dentro de uma borda) e o padding só desalinhava o título da
     * borda esquerda da tabela logo abaixo.
     */
    semPaddingLateral: {
      type: Boolean,
      default: false,
    },
    /**
     * [feedback do usuário, 02/09] A faixa de fundo que `open-questions.vue`
     * já monta na mão (`.ia-block-card__cabecalho--fita`, global em
     * `_ia-block-card.sass`) — separa o cabeçalho do corpo por COR, não só
     * por borda. Vira prop aqui pra virar reusável: o card de confirmação
     * de escrita (`ia-confirm-action.vue`) pediu o mesmo desenho.
     *
     * Junto com a faixa vem a ESCALA de título da família `fita`
     * (`.ia-block-card__titulo`, 12px) — não a de `.ia-block-card__nome`, que
     * é do vocabulário de LEITURA. `fita` escolhe a classe do título sozinha,
     * e `compacto` deixa de importar quando ela está ligada.
     */
    fita: {
      type: Boolean,
      default: false,
    },
    /**
     * O prefixo dos `data-teste` desta instância (`onboarding` →
     * `onboarding-colapso` no gatilho, `onboarding-sobretitulo` na linha de
     * cima). Vazio (default) não desenha os atributos.
     *
     * Existe porque o gatilho é DAQUI e quem precisa clicá-lo no teste é o
     * cartão lá fora — sem o prefixo, os vários cartões do thread dividiriam
     * o mesmo marcador e o E2E não saberia qual deles alternou.
     */
    prefixoDeTeste: {
      type: String,
      default: '',
    },
    /**
     * A ESCALA de respiro do cartão. Repassada pela `cartao-casca-fluida.vue`,
     * que a mantém igual no cabeçalho e no corpo — os dois recolhem juntos, e
     * paddings diferentes nas duas metades leriam como duas caixas.
     *
     * - `thread` — o cartão de leitura no meio da conversa (14px laterais, o
     *   mesmo recuo que os ~24 cartões já tinham).
     * - `etapa` — a etapa do onboarding: 24px, MEDIDO do que existia antes de
     *   o movimento ser extraído para a moldura (owner, 19/09/2026: *"os cards
     *   do onboarding ficaram sem padding ou diminuíram muito"* — foi
     *   exatamente esta conta que a extração tinha perdido).
     */
    escala: {
      type: String,
      default: 'thread',
      validator: (valor) => ['thread', 'etapa'].indexOf(valor) > -1,
    },
  },
  computed: {
    testeGatilho() {
      if (!this.clicavel || !this.prefixoDeTeste) return null
      return this.prefixoDeTeste + '-colapso'
    },
    testeSobretitulo() {
      return this.prefixoDeTeste ? this.prefixoDeTeste + '-sobretitulo' : null
    },
    /**
     * O vocabulário de SEÇÃO: título de verdade abrindo um bloco abaixo.
     *
     * É ele que liga a serif, o sobretítulo e o respiro maior — as três peças
     * do mock do owner. `fita`/`compacto` são a outra família (uma linha só) e
     * ficam de fora por definição.
     */
    ehSecao() {
      return Boolean(this.titulo) && !this.compacto && !this.fita
    },
    /**
     * Ids por INSTÂNCIA para `aria-labelledby`/`aria-describedby`.
     *
     * ⚠️ `_uid` porque id é global no documento e o thread desenha vários
     * cartões ao mesmo tempo: um id fixo faria o gatilho de todos apontar para
     * o título do primeiro. Mesmo motivo do id do `<linearGradient>` do
     * `ia-sparkline.vue`.
     */
    idDoTitulo() {
      return 'cartao-cabecalho-titulo-' + this._uid
    },
    idDasMetricas() {
      return 'cartao-cabecalho-metricas-' + this._uid
    },
    /**
     * Sem linha de métricas, o gatilho não aponta para um id inexistente.
     *
     * ⚠️ `ehSecao` junto: os ids do título e das métricas só são RENDERIZADOS
     * no vocabulário de seção. Sem a guarda, um cabeçalho `compacto` clicável
     * apontaria `aria-describedby` para um id que não existe no documento — e
     * um leitor de tela simplesmente não lê nada, sem erro nenhum na tela.
     */
    descritoPor() {
      if (!this.clicavel || !this.ehSecao) return null
      return this.temMetricas ? this.idDasMetricas : null
    },
    /**
     * Pré-computado porque o template não chama função (regra do repo) — e
     * porque ler `$slots` no template seria reavaliado a cada render.
     */
    temMetricas() {
      return Boolean(this.$slots.subtitulo || this.$scopedSlots.subtitulo)
    },
    /**
     * As métricas colapsam JUNTO do corpo (não somem por `v-if`) — e fora da
     * árvore de acessibilidade quando fechadas, porque uma caixa de altura
     * zero ainda é lida por leitor de tela.
     */
    metricasEscondidas() {
      return String(this.clicavel && !this.aberto)
    },
  },
  methods: {
    clicar() {
      if (!this.clicavel) return
      this.$emit('clicar')
    },
  },
}
</script>

<template>
  <div
    class="cartao-cabecalho"
    :class="{
      'cartao-cabecalho--sem-padding-lateral': semPaddingLateral,
      'cartao-cabecalho--titulo-grande': ehSecao,
      'cartao-cabecalho--colapsavel': clicavel,
      'cartao-cabecalho--recolhido': clicavel && !aberto,
      'cartao-cabecalho--escala-etapa': escala === 'etapa',
    }"
  >
    <div
      class="ia-block-card__cabecalho"
      :class="{
        'ia-block-card__cabecalho--clicavel': clicavel,
        'ia-block-card__cabecalho--fita': fita,
      }"
      :role="clicavel ? 'button' : null"
      :tabindex="clicavel ? '0' : null"
      :aria-expanded="clicavel ? String(aberto) : null"
      :aria-labelledby="clicavel && ehSecao ? idDoTitulo : null"
      :aria-describedby="descritoPor"
      :data-teste="testeGatilho"
      @click="clicar"
      @keydown.enter="clicar"
      @keydown.space.prevent="clicar"
    >
      <icon-svg
        v-if="icone"
        :name="icone"
        :size="14"
        :color="corDoIcone"
        class="ia-block-card__icone"
      />

      <!-- Sobretítulo e título empilham; tudo que vier depois (meta, ação)
           divide a faixa com a pilha, não com o título sozinho. -->
      <span v-if="ehSecao" class="cartao-cabecalho__titulos">
        <!-- ⚠️ Colapsa, nunca sai por `v-if`: ver a nota de `__sobretitulo`
             no `<style>`. -->
        <span
          v-if="sobretitulo"
          class="cartao-cabecalho__sobretitulo"
          :aria-hidden="metricasEscondidas"
        >
          <span class="az-ds-label" :data-teste="testeSobretitulo">{{
            sobretitulo
          }}</span>
        </span>
        <span
          :id="idDoTitulo"
          class="ia-block-card__nome cartao-cabecalho__titulo ia-titulo-de-cartao"
          :data-teste="testeTitulo || null"
          >{{ titulo }}</span
        >
        <!-- O resumo do recolhido: mesma mecânica de altura automática do
             sobretítulo (`grid-template-rows`), no sentido contrário. -->
        <span
          v-if="resumo"
          class="cartao-cabecalho__resumo"
          :aria-hidden="String(!(clicavel && !aberto))"
          data-teste="cartao-resumo"
        >
          <span class="ia-texto-auxiliar cartao-cabecalho__resumo-texto text-truncate d-block">{{ resumo }}</span>
        </span>

        <!-- ★ [owner, 19/09/2026] A linha de métricas mora DENTRO da coluna do
             título, e não como irmã do cabeçalho: *"as três linhas alinham
             pela MESMA margem esquerda do título"*. Fora dela, o ícone (14px
             + 8px de gap) recuava o título e a métrica ficava deslocada à
             esquerda dele — visível no print do checklist R2.
             ⚠️ `<span>`, e não `<p>`: aqui dentro ele é item de uma coluna
             flex dentro da faixa do cabeçalho, e um bloco com margem própria
             (que é o que `<p>` traz do reset) somaria um degrau ao colapso. -->
        <span
          v-if="temMetricas"
          :id="idDasMetricas"
          class="ia-block-card__subtitulo cartao-cabecalho__metricas"
          :aria-hidden="metricasEscondidas"
        >
          <slot name="subtitulo" />
        </span>
      </span>

      <span
        v-else-if="titulo && fita"
        class="ia-block-card__titulo"
        :data-teste="testeTitulo || null"
        >{{ titulo }}</span
      >
      <span
        v-else-if="titulo"
        class="ia-block-card__nome ia-block-card__nome--compacto"
        :data-teste="testeTitulo || null"
        >{{ titulo }}</span
      >
      <slot v-else name="titulo" />

      <slot name="meta" />
      <span v-if="$slots.cornerAction" class="cartao-cabecalho__acao">
        <slot name="cornerAction" />
      </span>
    </div>

    <!-- No vocabulário de STATUS/AÇÃO (`compacto`/`fita`) não há coluna de
         título onde encaixar a linha de apoio: ela continua irmã do
         cabeçalho, exatamente como sempre esteve. -->
    <p v-if="temMetricas && !ehSecao" class="ia-block-card__subtitulo mb-0">
      <slot name="subtitulo" />
    </p>
  </div>
</template>

<style lang="sass" scoped>
// ⚠️ O MESMO tempo/easing do `.expand-transition-*-active` da Vuetify
// (`vuetify.css`). Cabeçalho e corpo têm de recolher como uma peça só: se o
// respiro do gatilho ou o sobretítulo trocarem de um quadro para o outro
// enquanto o corpo desliza por 300ms, o título SALTA — e lê como travada,
// mesmo sem nenhum jank de pintura. É o corolário do item 3 da receita do
// `CLAUDE.md` da raiz ("v-expand-transition: Como Evitar Travamento"), e foi o
// que a casca do onboarding mediu em 18/09/2026.
//
// ⚠️ E NÃO é o anti-padrão de atrasar classe por `@after-leave`: nada aqui
// espera hook nenhum — é `transition` de CSS, que não fica presa num estado se
// o efeito for interrompido.
$colapso-duracao: 300ms
$colapso-easing: cubic-bezier(0.25, 0.8, 0.5, 1)

// O respiro lateral é o de sempre (14px): mudá-lo deslocaria na horizontal os
// ~20 cartões que já usam esta casca, e o mock só pede mais ar na VERTICAL.
// ★ MEDIDO, não escolhido: `thread` é o recuo que os ~24 cartões de leitura já
// tinham (`.ia-block-card__cabecalho`, 14px laterais); `etapa` é o que a casca
// do onboarding tinha antes de o movimento ser extraído para a moldura
// (`$respiro-do-cartao: 24px` / `$respiro-do-recolhido: 12px`). Mudar um deles
// muda a tela de alguém — os dois são reproduções, não decisões novas.
// ★ [owner, 19/09/2026] *"falta o padding X para os componentes com borda,
// olha como está feio"*. Eram 14px — o recuo de quando o cabeçalho FLUTUAVA
// sem caixa (27 dos 49 cartões passam `sem-borda`, e hoje esses ficam em 0).
// Com moldura de verdade, 14px encostam o título nela. 24px é o mesmo valor da
// escala `etapa`, que é justamente o cartão bordado que o owner usa de
// referência — uma medida só para os dois, em vez de duas quase iguais.
$respiro-lateral: 24px
$respiro-aberto: 16px

// ⚠️ O recolhido é ASSIMÉTRICO de propósito — 14px em cima, 8px embaixo.
//
// Não é descuido, e o número saiu de MEDIÇÃO da fonte, não do olho.
// ⚠️ A medição foi feita com a SERIF ('Instrument Serif'), que era a família do
// título até 21/09/2026. Hoje a família vem de `--ia-font-familia-destaque`
// (`design/_ia-texto.sass`, Geist), cujas métricas são outras: os números
// abaixo NÃO foram recalculados — quem notar o título fora do centro no
// recolhido remede na tela antes de mexer em `$respiro-recolhido-topo`/`-base`.
// 'Instrument Serif' devolve, a 28px: `fontBoundingBoxAscent` 28 e
// `fontBoundingBoxDescent` 9 — ou seja, ascent 1.0em contra descent 0.32em.
// A caixa de linha centra a caixa EM, não os glifos, então com `line-height`
// 1.2 o bloco de glifos assenta `0.054em` ACIMA do centro (1,3px a 24px).
// Compensar pede o DOBRO disso no padding: topo = base + 3px.
//
// Isto já existia por acidente — os 6px de `margin-top` do `--titulo-grande`
// compensavam sem dizer o nome —, e quando eu os zerei (achando que era só
// altura sobrando) o owner viu na hora: *"o título foi mais para cima,
// totalmente desalinhado"*. A primeira versão explícita errou para o outro
// lado (+6, o dobro do necessário). Agora é +3, e a conta está acima.
$respiro-recolhido-topo: 12px
$respiro-recolhido-base: 9px

// [owner, 19/09/2026] *"adicione um espaço abaixo do título/subtítulo, pois
// está bem colado"*. Antes era `0`, e o único respiro até o conteúdo era o
// `padding-top` do corpo (10px no `thread`) — com o título em serif de 28px e
// a linha de métricas logo abaixo, a tabela encostava.
// ⚠️ Vai no CABEÇALHO, nunca no corpo: o corpo é o elemento que o
// `v-expand-transition` anima, e padding nele é um PISO que `height: 0` não
// atravessa (a travada do item 0 da receita, no `<style>` da casca fluida).
$respiro-abaixo-do-titulo: 8px

$respiro-lateral-etapa: 24px
$respiro-aberto-etapa: 24px
$respiro-recolhido-etapa: 12px

// [feedback do usuário] Respiro ACIMA do título — com o título maior, o
// padding-top de sempre (10px, herdado do `.ia-block-card__cabecalho`
// compartilhado com os cartões "de escrita") ficava apertado contra o topo do
// cartão. Empurra o cabeçalho inteiro (ícone+título+ação) pra baixo como
// bloco, sem tocar no padding compartilhado nem desalinhar ícone/título
// dentro da própria linha flex.
//
// ★ [bug corrigido] Só no vocabulário de SEÇÃO. Nos cartões `compacto`
// (ação/status) e num cabeçalho SÓ de slot essa margem não tem contrapeso
// embaixo — o conteúdo ficava puxado pra baixo dentro da pill, perdendo o
// centro vertical.
.cartao-cabecalho--titulo-grande
  margin-top: 6px

// ── O GATILHO (só quando `clicavel`) ───────────────────────────────────────
//
// Os resets de borda/fundo/alinhamento NÃO estão aqui: a classe global
// `.ia-block-card__cabecalho` já os traz, e `--clicavel` já dá o `cursor`.
.cartao-cabecalho--colapsavel
  .ia-block-card__cabecalho
    padding: $respiro-aberto $respiro-lateral $respiro-abaixo-do-titulo
    // Acompanha o corpo em vez de saltar — ver a nota de `$colapso-duracao`.
    transition: padding $colapso-duracao $colapso-easing

  .cartao-cabecalho__metricas
    transition: grid-template-rows $colapso-duracao $colapso-easing, opacity $colapso-duracao $colapso-easing

// A escala da ETAPA do onboarding, reproduzida do que existia. Vem DEPOIS das
// regras de `thread` para sobrescrevê-las com a mesma especificidade.
.cartao-cabecalho--escala-etapa
  &.cartao-cabecalho--colapsavel .ia-block-card__cabecalho
    padding: $respiro-aberto-etapa $respiro-lateral-etapa $respiro-abaixo-do-titulo

  &.cartao-cabecalho--recolhido .ia-block-card__cabecalho
    padding: $respiro-recolhido-etapa $respiro-lateral-etapa

  // ⚠️ A etapa não tem `margin-top` extra: os 24px do topo já são o respiro
  // inteiro. Somar os 6px do `--titulo-grande` daria 30px e o cartão abriria
  // mais folgado em cima do que embaixo.
  &.cartao-cabecalho--titulo-grande
    margin-top: 0

// Sem corpo abaixo, o cabeçalho fecha o cartão — num respiro menor, para ler
// como uma linha de histórico e não como um cartão vazio.
.cartao-cabecalho--recolhido
  .ia-block-card__cabecalho
    padding: $respiro-recolhido-topo $respiro-lateral $respiro-recolhido-base

  // A linha de métricas colapsa a altura, mas a margem dela NÃO colapsa junto:
  // sobravam 2px mortos embaixo do título, que desequilibravam o centro do
  // chevron em relação a ele.
  .cartao-cabecalho__metricas
    margin-top: 0

  // [owner, 19/09/2026] *"no modo colapsado, quando tem borda, está ficando
  // bem grande a div"*. Os 6px do `--titulo-grande` existem para dar ar ao
  // título grande quando o cartão está ABERTO; recolhido eles só somavam ao
  // padding de cima (6+10) contra 10 embaixo — o cartão era 6px mais alto E
  // desalinhado na vertical. Aberto nada muda.
  &.cartao-cabecalho--titulo-grande
    margin-top: 0

  .cartao-cabecalho__sobretitulo
    grid-template-rows: 0fr
    padding-bottom: 0
    opacity: 0

  .cartao-cabecalho__metricas
    grid-template-rows: 0fr
    opacity: 0

  // O resumo ABRE no recolhido (é o único lugar em que ele existe).
  .cartao-cabecalho__resumo
    grid-template-rows: 1fr
    padding-top: 2px
    opacity: 1

// O resumo do recolhido: fechado enquanto o cartão está aberto, com a MESMA
// receita de altura automática do sobretítulo — ver a nota abaixo.
.cartao-cabecalho__resumo
  display: grid
  grid-template-rows: 0fr
  padding-top: 0
  overflow: hidden
  opacity: 0
  transition: grid-template-rows $colapso-duracao $colapso-easing, padding-top $colapso-duracao $colapso-easing, opacity $colapso-duracao $colapso-easing

  > *
    min-height: 0

.cartao-cabecalho__resumo-texto
  color: var(--neutral-600)

// A pilha sobretítulo+título. O título é quem cede espaço quando a ação
// divide a faixa com ele; ícone, meta e chevron nunca encolhem.
.cartao-cabecalho__titulos
  display: flex
  flex-direction: column
  min-width: 0
  flex: 1 1 auto

// ⚠️ O respiro de 4px até o título vive AQUI, e não como `gap` do
// `__titulos`: item de grid com altura zero ainda soma o `gap` do pai, e
// sobrariam 4px de salto justamente no fim do recolher. Como padding da
// própria caixa, ele colapsa junto.
//
// `grid-template-rows: 1fr → 0fr` é a forma exata de animar altura AUTOMÁTICA:
// `max-height` exigiria um teto chutado, e o rótulo ficaria parado no começo
// do efeito até o teto cruzar a altura real. Navegador que não interpola `fr`
// cai na troca instantânea — degradação, não regressão.
.cartao-cabecalho__sobretitulo
  display: grid
  grid-template-rows: 1fr
  padding-bottom: 4px
  overflow: hidden
  transition: grid-template-rows $colapso-duracao $colapso-easing, padding-bottom $colapso-duracao $colapso-easing, opacity $colapso-duracao $colapso-easing

  // Sem isto o filho impõe a própria altura mínima e a linha nunca fecha.
  > *
    min-height: 0

// ★ A receita do título mora em `design/_ia-texto.sass` (`.ia-titulo-de-cartao`),
// e não aqui: [owner, 19/09/2026] *"vamos criar tipografia única para os cards
// e reutilizar"*. Eram os mesmos quatro valores escritos à mão aqui e em
// `components/conexao-cabecalho/`.
//
// ⚠️ A classe BEM (`.cartao-cabecalho__titulo`) FICA no template mesmo sem
// regra: quatro casos de `cartao-cabecalho.unit.js`/`cartao-casca.unit.js` a
// usam como seletor, e um deles lê o `id` por ela (o `aria-labelledby`).
//
// ⚠️ `.ia-titulo-de-cartao` é global (0,1,0), igual a `.ia-block-card__nome`
// (20px/700) que está na MESMA tag — quem vence é a ordem: `_ia-texto` entra
// na linha 104 de `design/index.sass` e `_ia-block-card` na 23. Mover
// qualquer um dos dois devolve o título a 20px em negrito, sem erro nenhum.

// A linha de métricas, dentro da coluna do título.
//
// ⚠️ `padding: 0` porque a classe global `.ia-block-card__subtitulo` traz
// `0 14px 10px` — o recuo de quando ela era IRMÃ do cabeçalho. Aqui quem já dá
// o recuo lateral é o padding do próprio gatilho, e repeti-lo empurraria a
// métrica 14px para dentro do título.
//
// ★ A cor é declarada AQUI, e não herdada: o cabeçalho é um `<button>`, e
// `color` de botão herda do agente do usuário em alguns navegadores — foi o
// que deixou a linha do checklist R2 na cor de destaque em vez de muted
// (owner, 19/09/2026). `--neutral-600` é o mesmo tom que a classe global já
// pedia; `--primary` ali competiria com o título e ainda é white-label.
.cartao-cabecalho__metricas
  display: grid
  grid-template-rows: 1fr
  overflow: hidden
  padding: 0
  margin-top: 2px
  color: var(--neutral-600)

  // ★★ ESTA LINHA É O CONSERTO DO "título não centralizado no eixo Y"
  // (owner, 19/09/2026, com o inspetor aberto no print).
  //
  // Sem ela o filho impõe a PRÓPRIA altura mínima e a faixa NUNCA fecha: no
  // cartão recolhido, `grid-template-rows: 0fr` não tinha efeito e a linha de
  // métricas continuava ocupando ~16px — invisível (`opacity: 0`) e logo
  // ABAIXO do título. O título ficava no alto de uma coluna que era 16px mais
  // alta que ele, e nenhum ajuste de padding resolvia, porque o espaço sobrava
  // DENTRO da coluna, não em volta dela.
  //
  // ⚠️ `.cartao-cabecalho__sobretitulo` já tinha a mesma linha, com o mesmo
  // comentário ("sem isto o filho impõe a própria altura mínima"). Esta regra
  // nasceu depois, copiando o resto do padrão e deixando justamente a linha
  // que faz o colapso existir — o tipo de omissão que não levanta erro nenhum.
  > *
    min-height: 0

  // Conteúdo do slot não pode trazer cor própria — a régua da linha é uma só.
  *
    color: inherit

// Empurra a ação pro canto direito e alinha os itens dela (ex: hiperlink +
// seta de colapso) horizontalmente, com o mesmo respiro entre eles — não
// depende mais dos combinators `.ia-block-card__meta + .x` do partial global.
.cartao-cabecalho__acao
  display: flex
  align-items: center
  gap: 8px
  margin-left: auto

// ⚠️⚠️ **O chevron NÃO leva `align-self: flex-start` — tentativa feita,
// medida e revertida no mesmo dia (19/09/2026).**
//
// A ideia era pô-lo na altura do sobretítulo, como no mock estático. Funciona
// com o cartão ABERTO e quebra com ele RECOLHIDO: `--recolhido` colapsa o
// sobretítulo e as métricas (`grid-template-rows: 0fr`), sobra o título
// sozinho, e não há mais nada lá em cima com que alinhar. O owner viu na tela:
// *"no modo colapsado a flecha não está alinhada"*.
//
// A conta, do próprio CSS: a caixa do título é 28px × 1.2 = **33,6px**, o
// ícone tem **14px**, e `flex-start` o põe no topo dela — **9,8px** acima do
// centro ((33,6 − 14) / 2). Bate com o "~10px" relatado no print.
//
// **Caminhos descartados, e por quê:**
//
// - `align-self: center` só no `--recolhido`: a classe troca de valor no MESMO
//   quadro em que a animação começa, com a coluna ainda em 54px — o chevron
//   pularia ~20px para baixo e só então deslizaria de volta. É o salto do item
//   3 da receita, na direção contrária.
// - `flex-start` + `margin-top` animado no mesmo `$colapso-duracao`: contínuo,
//   mas compra um valor mágico (os 9,8px) que passa a depender do tamanho do
//   título — a primeira mudança de escala do serif o desalinha em silêncio.
// - Mover o chevron para DENTRO da coluna de títulos: aí ele alinharia pelo
//   título de verdade, mas deixaria de encostar na borda direita da faixa.
//
// **O que fica é o `align-items: center` do `.ia-block-card__cabecalho`**, sem
// nenhum `align-self` — que é exatamente o que a casca do onboarding fazia
// antes desta extração (`d-flex align-center` no gatilho, `margin-left: 0` no
// chevron e nada mais). Ele é contínuo POR CONSTRUÇÃO: não há classe de
// alinhamento trocando de valor, só a altura da coluna mudando de 54px para
// 33,6px ao longo dos mesmos 300ms — o chevron acompanha o centro dela do
// começo ao fim e termina centrado no título. O preço é o cartão ABERTO não
// pôr o chevron exatamente na linha do sobretítulo, como o mock estático
// mostra; é o preço que o cartão que o owner chamou de "lindo" já pagava.

// ★ [owner, 19/09/2026] *"no modo SEM BORDA, os cards não podem ter padding
// no eixo X"*. Sem caixa, o recuo lateral não recua de nada: o cabeçalho ficava
// 14px à direita do conteúdo, que já encosta na borda da bolha (print do
// `tarefas/lista`). Era 4px até aqui — e nem esse valia, porque o `padding`
// SHORTHAND de `--colapsavel`/`--recolhido` vem depois e tem a MESMA
// especificidade (0,2,0), então vencia com os 14px.
//
// ⚠️ Por isso este bloco mora no FIM do arquivo e usa LONGHAND: o fim é o que
// o faz vencer o shorthand, e o longhand é o que o deixa tocar só o eixo X —
// o respiro vertical dos dois estados continua intacto. Mover para cima
// desfaz a correção sem nada ficar vermelho.
//
// ⚠️ `>` no subtítulo: no vocabulário de SEÇÃO ele mora DENTRO do gatilho (que
// já recuou), e um segundo recuo o empurraria para dentro do título. Só a
// linha irmã — a dos cartões `compacto`/`fita` — paga o recuo aqui.
.cartao-cabecalho--sem-padding-lateral
  .ia-block-card__cabecalho,
  > .ia-block-card__subtitulo
    padding-left: 0
    padding-right: 0

.ia-block-card__cabecalho--clicavel
  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: -2px

// ★ O `v-expand-transition` é JS da Vuetify e NÃO lê `prefers-reduced-motion`
// sozinho — quem anima é o `transition` de `.expand-transition-*-active`
// (`vuetify.css`), e quem se desliga aqui são as peças do cabeçalho que o
// acompanham.
@media (prefers-reduced-motion: reduce)
  .cartao-cabecalho--colapsavel
    .ia-block-card__cabecalho,
    .cartao-cabecalho__sobretitulo,
    .cartao-cabecalho__metricas
      transition: none
</style>
